import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useNotifications } from './NotificationContext';
import { chatAPI } from '../services/api';
import { chatWebSocket, WSMessage } from '../services/chatWebSocket';
import {
  Conversation, ChatMessage, TypingUser,
  WSOutgoingMessage, TypingIndicator, PresenceUpdate, ReadReceipt
} from '../types/chat';

// ============== State Interface ==============
interface ChatState {
  conversations: Conversation[];
  activeConversationId: number | null;
  typingUsers: TypingUser[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

// ============== Initial State ==============
const initialState: ChatState = {
  conversations: [],
  activeConversationId: null,
  typingUsers: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

// ============== Action Types ==============
type ChatAction =
  | { type: 'SET_CONVERSATIONS'; payload: Conversation[] }
  | { type: 'SET_ACTIVE_CONVERSATION'; payload: number | null }
  | { type: 'ADD_CONVERSATION'; payload: Conversation }
  | { type: 'UPDATE_CONVERSATION'; payload: Conversation }
  | { type: 'ADD_MESSAGE'; payload: ChatMessage }
  | { type: 'MARK_MESSAGE_DELETED'; payload: { messageId: number; conversationId: number } }
  | { type: 'INCREMENT_UNREAD'; payload: { conversationId: number } }
  | { type: 'SET_TYPING_USERS'; payload: TypingUser[] }
  | { type: 'ADD_TYPING_USER'; payload: TypingUser }
  | { type: 'REMOVE_TYPING_USER'; payload: number }
  | { type: 'SET_UNREAD_COUNT'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'UPDATE_USER_PRESENCE'; payload: { userId: number; isOnline: boolean } };

// ============== Reducer ==============
function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.payload };

    case 'SET_ACTIVE_CONVERSATION':
      return { ...state, activeConversationId: action.payload, typingUsers: [] };

    case 'ADD_CONVERSATION': {
      // Check if conversation already exists
      if (state.conversations.find(c => c.id === action.payload.id)) {
        return state;
      }
      return { ...state, conversations: [action.payload, ...state.conversations] };
    }

    case 'UPDATE_CONVERSATION': {
      const updatedConversations = state.conversations.map(c =>
        c.id === action.payload.id ? action.payload : c
      );
      // Sort by last message time
      updatedConversations.sort((a, b) => {
        const dateA = a.last_message?.created_at || a.updated_at || a.created_at;
        const dateB = b.last_message?.created_at || b.updated_at || b.created_at;
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });
      return { ...state, conversations: updatedConversations };
    }

    case 'ADD_MESSAGE': {
      const conversationId = action.payload.conversation_id;
      const updatedConversations = state.conversations.map(c => {
        if (c.id === conversationId) {
          return { ...c, last_message: action.payload };
        }
        return c;
      });
      // Sort by last message time
      updatedConversations.sort((a, b) => {
        const dateA = a.last_message?.created_at || a.updated_at || a.created_at;
        const dateB = b.last_message?.created_at || b.updated_at || b.created_at;
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });
      return { ...state, conversations: updatedConversations };
    }

    case 'MARK_MESSAGE_DELETED': {
      // Update last_message in conversation if the deleted message was the last one
      const { messageId, conversationId } = action.payload;
      const updatedConversations = state.conversations.map(c => {
        if (c.id === conversationId && c.last_message?.id === messageId) {
          return {
            ...c,
            last_message: {
              ...c.last_message,
              is_deleted: true,
              content: null,
              attachments: []
            }
          };
        }
        return c;
      });
      return { ...state, conversations: updatedConversations };
    }

    case 'INCREMENT_UNREAD': {
      const { conversationId } = action.payload;
      const updatedConversations = state.conversations.map(c => {
        if (c.id === conversationId) {
          return { ...c, unread_count: (c.unread_count || 0) + 1 };
        }
        return c;
      });
      return {
        ...state,
        conversations: updatedConversations,
        unreadCount: state.unreadCount + 1
      };
    }

    case 'SET_TYPING_USERS':
      return { ...state, typingUsers: action.payload };

    case 'ADD_TYPING_USER': {
      if (state.typingUsers.find(u => u.user_id === action.payload.user_id)) {
        return state;
      }
      return { ...state, typingUsers: [...state.typingUsers, action.payload] };
    }

    case 'REMOVE_TYPING_USER':
      return {
        ...state,
        typingUsers: state.typingUsers.filter(u => u.user_id !== action.payload)
      };

    case 'SET_UNREAD_COUNT':
      return { ...state, unreadCount: action.payload };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'UPDATE_USER_PRESENCE': {
      const { userId, isOnline } = action.payload;
      const updatedConversations = state.conversations.map(c => {
        if (c.other_participant && c.other_participant.id === userId) {
          return {
            ...c,
            other_participant: {
              ...c.other_participant,
              is_online: isOnline,
              last_seen_at: isOnline ? null : new Date().toISOString(),
            },
          };
        }
        return c;
      });
      return { ...state, conversations: updatedConversations };
    }

    default:
      return state;
  }
}

// ============== Context Interface ==============
interface ChatContextType {
  state: ChatState;
  loadConversations: () => Promise<void>;
  setActiveConversation: (conversationId: number | null) => void;
  sendMessage: (content: string, attachmentIds?: number[]) => Promise<ChatMessage | null>;
  sendTypingIndicator: () => void;
  deleteMessage: (messageId: number) => Promise<boolean>;
  markMessagesAsRead: (messageIds: number[]) => Promise<void>;
  isWidgetOpen: boolean;
  openWidget: () => void;
  closeWidget: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

// ============== Helper Functions ==============
// Add other_participant field to conversation
const enrichConversation = (conversation: Conversation, currentUserId: number): Conversation => {
  const otherParticipant = conversation.participants.find(
    p => p.user_id !== currentUserId
  );
  return {
    ...conversation,
    other_participant: otherParticipant?.user,
  };
};

// ============== Provider ==============
interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const { showNotification, playNotificationSound, permission, requestPermission } = useNotifications();

  // Chat widget open/close state (lifted from ChatWidget so the header button can control it)
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const openWidget = useCallback(() => setIsWidgetOpen(true), []);
  const closeWidget = useCallback(() => setIsWidgetOpen(false), []);

  // Close the widget when the user logs out
  useEffect(() => {
    if (!user) {
      setIsWidgetOpen(false);
    }
  }, [user]);

  // Track last known message IDs to detect new messages
  const lastMessageIdsRef = useRef<Map<number, number>>(new Map());
  const hasRequestedPermissionRef = useRef(false);
  const isLoadingRef = useRef(false);
  const lastFetchAtRef = useRef<number>(0);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!user) return;
    if (isLoadingRef.current) return; // Prevent concurrent calls
    isLoadingRef.current = true;
    lastFetchAtRef.current = Date.now();
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const result = await chatAPI.getConversations();
      // Enrich conversations with other_participant
      const enrichedConversations = result.conversations.map(c =>
        enrichConversation(c, user.id)
      );

      // Check for new messages and show notifications
      enrichedConversations.forEach(conversation => {
        if (conversation.last_message) {
          const lastKnownMessageId = lastMessageIdsRef.current.get(conversation.id);
          const currentMessageId = conversation.last_message.id;

          // Update last known message ID FIRST to prevent duplicate notifications
          lastMessageIdsRef.current.set(conversation.id, currentMessageId);

          // If there's a new message we haven't seen
          if (lastKnownMessageId !== undefined && currentMessageId > lastKnownMessageId) {
            // Only notify if message is from someone else AND not viewing that conversation
            if (conversation.last_message.sender_id !== user.id && conversation.id !== state.activeConversationId) {
              // Play notification sound
              playNotificationSound();

              // Show browser notification
              const senderName = conversation.other_participant
                ? conversation.other_participant.full_name
                : 'Nouveau message';
              const messagePreview = conversation.last_message.content
                ? conversation.last_message.content.substring(0, 50) + (conversation.last_message.content.length > 50 ? '...' : '')
                : 'Fichier envoyé';

              showNotification(senderName, {
                body: messagePreview,
                tag: `chat-${conversation.id}`, // Prevent duplicate notifications
              });
            }
          }
        }
      });

      dispatch({ type: 'SET_CONVERSATIONS', payload: enrichedConversations });

      // Calculate total unread
      const totalUnread = enrichedConversations.reduce((sum, c) => sum + c.unread_count, 0);
      dispatch({ type: 'SET_UNREAD_COUNT', payload: totalUnread });
    } catch (error) {
      console.error('Failed to load conversations:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load conversations' });
    } finally {
      isLoadingRef.current = false;
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [user, state.activeConversationId, showNotification, playNotificationSound]);

  // Set active conversation
  const setActiveConversation = useCallback((conversationId: number | null) => {
    // Leave the previous conversation if any
    if (state.activeConversationId && state.activeConversationId !== conversationId) {
      chatWebSocket.leaveConversation(state.activeConversationId);
    }

    // Enter the new conversation if any
    if (conversationId) {
      chatWebSocket.enterConversation(conversationId);
    }

    dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: conversationId });
  }, [state.activeConversationId]);

  // Send a message
  const sendMessage = useCallback(async (content: string, attachmentIds?: number[]): Promise<ChatMessage | null> => {
    if (!state.activeConversationId) return null;

    try {
      const messageType = attachmentIds && attachmentIds.length > 0 ? 'file' : 'text';
      const message = await chatAPI.sendMessage(
        state.activeConversationId,
        content || undefined,
        messageType,
        attachmentIds
      );
      dispatch({ type: 'ADD_MESSAGE', payload: message });
      return message;
    } catch (error) {
      console.error('Failed to send message:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to send message' });
      return null;
    }
  }, [state.activeConversationId]);

  // Send typing indicator via WebSocket
  const sendTypingIndicator = useCallback(() => {
    if (state.activeConversationId) {
      chatWebSocket.sendTyping(state.activeConversationId, true);
      // Auto stop typing after 3 seconds
      setTimeout(() => {
        if (state.activeConversationId) {
          chatWebSocket.sendTyping(state.activeConversationId, false);
        }
      }, 3000);
    }
  }, [state.activeConversationId]);

  // Delete a message
  const deleteMessage = useCallback(async (messageId: number): Promise<boolean> => {
    try {
      await chatAPI.deleteMessage(messageId);
      return true;
    } catch (error) {
      console.error('Failed to delete message:', error);
      return false;
    }
  }, []);

  // Mark messages as read
  const markMessagesAsRead = useCallback(async (messageIds: number[]) => {
    if (messageIds.length === 0) return;

    try {
      // Mark the last message as read (this updates last_read_at for the conversation)
      const lastMessageId = Math.max(...messageIds);
      await chatAPI.markMessageRead(lastMessageId);

      // Refresh conversations to update unread counts
      await loadConversations();
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  }, [loadConversations]);

  // Request notification permission on first load
  useEffect(() => {
    if (user && !hasRequestedPermissionRef.current && permission === 'default') {
      hasRequestedPermissionRef.current = true;
      requestPermission();
    }
  }, [user, permission, requestPermission]);

  // Update browser tab title with unread count
  useEffect(() => {
    const baseTitle = 'MAF ERP';
    if (state.unreadCount > 0) {
      document.title = `${baseTitle} (${state.unreadCount})`;
    } else {
      document.title = baseTitle;
    }
  }, [state.unreadCount]);

  // Initial load
  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user, loadConversations]);

  // Visibility-based refresh - only refresh when user returns to the tab
  // Debounced: skip the refresh if we already fetched within VISIBILITY_REFRESH_DEBOUNCE_MS,
  // since real-time delivery is handled by the WebSocket — this fetch is a resync safety net.
  useEffect(() => {
    if (!user) return;

    const VISIBILITY_REFRESH_DEBOUNCE_MS = 30_000;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const elapsed = Date.now() - lastFetchAtRef.current;
      if (elapsed < VISIBILITY_REFRESH_DEBOUNCE_MS) return;
      loadConversations();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, loadConversations]);

  // Use refs to avoid effect re-running on callback changes
  const loadConversationsRef = useRef(loadConversations);
  const playNotificationSoundRef = useRef(playNotificationSound);
  const showNotificationRef = useRef(showNotification);
  const activeConversationIdRef = useRef(state.activeConversationId);

  useEffect(() => {
    loadConversationsRef.current = loadConversations;
  }, [loadConversations]);

  useEffect(() => {
    playNotificationSoundRef.current = playNotificationSound;
  }, [playNotificationSound]);

  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);

  useEffect(() => {
    activeConversationIdRef.current = state.activeConversationId;
  }, [state.activeConversationId]);

  // WebSocket connection and real-time updates
  useEffect(() => {
    if (!user) return;

    let mounted = true;

    // Token refresh function for reconnection
    const refreshToken = async (): Promise<string> => {
      const { token } = await chatAPI.getWsToken();
      return token;
    };

    const connectWebSocket = async () => {
      try {
        const { token } = await chatAPI.getWsToken();

        if (!mounted) return;

        // Set token refresh callback for reconnection
        chatWebSocket.setTokenRefreshCallback(refreshToken);
        chatWebSocket.connect(token);
      } catch (error) {
        // Silent fail - WebSocket will retry
      }
    };

    connectWebSocket();

    // Handle incoming WebSocket messages
    const unsubscribe = chatWebSocket.onMessage((message: WSMessage) => {
      switch (message.type) {
        case 'presence_update':
          // Real-time presence update
          dispatch({
            type: 'UPDATE_USER_PRESENCE',
            payload: {
              userId: message.data.user_id,
              isOnline: message.data.is_online,
            },
          });
          break;

        case 'new_message':
          // Real-time new message - update immediately without HTTP call
          const newMsg = message.data;

          // Only notify for messages from others AND when NOT viewing that conversation
          if (newMsg.sender_id !== user.id && newMsg.conversation_id !== activeConversationIdRef.current) {
            // Play notification sound
            playNotificationSoundRef.current();

            // Show browser notification
            showNotificationRef.current(newMsg.sender?.full_name || newMsg.sender_name || 'New message', {
              body: newMsg.content?.substring(0, 50) || 'File sent',
              tag: `chat-${newMsg.conversation_id}`,
            });

            // Increment unread count for this conversation
            dispatch({ type: 'INCREMENT_UNREAD', payload: { conversationId: newMsg.conversation_id } });
          }

          // Dispatch ADD_MESSAGE to update conversation's last_message immediately
          dispatch({ type: 'ADD_MESSAGE', payload: newMsg });
          break;

        case 'message_deleted':
          // Real-time message deletion - notify via custom event for UI update
          const deletedData = message.data;
          dispatch({
            type: 'MARK_MESSAGE_DELETED',
            payload: {
              messageId: deletedData.message_id,
              conversationId: deletedData.conversation_id
            }
          });
          // Emit custom event for components to update their local message state
          window.dispatchEvent(new CustomEvent('chat:message_deleted', {
            detail: {
              messageId: deletedData.message_id,
              conversationId: deletedData.conversation_id
            }
          }));
          break;

        case 'typing_start':
          if (message.data.conversation_id === activeConversationIdRef.current) {
            dispatch({
              type: 'ADD_TYPING_USER',
              payload: {
                user_id: message.data.user_id,
                user_name: message.data.user_name || 'Someone',
              },
            });
          }
          break;

        case 'typing_stop':
          dispatch({
            type: 'REMOVE_TYPING_USER',
            payload: message.data.user_id,
          });
          break;

        case 'message_read':
          // Could update read receipts UI if needed
          break;

        case 'error':
          console.error('WebSocket error:', message.data);
          break;
      }
    });

    // Cleanup on unmount or user change
    return () => {
      mounted = false;
      unsubscribe();
      // Leave active conversation before disconnecting
      if (activeConversationIdRef.current) {
        chatWebSocket.leaveConversation(activeConversationIdRef.current);
      }
      chatWebSocket.disconnect();
    };
  }, [user]); // Only depend on user - stable connection

  const value: ChatContextType = {
    state,
    loadConversations,
    setActiveConversation,
    sendMessage,
    sendTypingIndicator,
    deleteMessage,
    markMessagesAsRead,
    isWidgetOpen,
    openWidget,
    closeWidget,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;
